import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { TimeEntryForPlanningService } from '../time-entry-for-planning.service';
import { FormBuilder, FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { getCurrentUser } from 'app/core/store/currentUser/user.selectors';
import { CommonService } from '../../../common/services/common.service';
import { ConsultantDetailsService } from '../../../common/services/consultant-details.service';
import { firstValueFrom } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { SnackbarService } from '../../../common/services/snackbar.service';
import { Constants } from '../../../common/utilities/constants.config';

@Component({
  selector: 'app-plan-filter-panel',
  templateUrl: './plan-filter-panel.component.html',
  styleUrls: ['./plan-filter-panel.component.scss']
})
export class PlanFilterPanelComponent implements OnInit, OnChanges {

  @Input() collapsedSummary = false;
  @Output() collapsedChange = new EventEmitter<boolean>();

  manuallyExpanded = false;

  entryForm: FormGroup;
  isFilterOpen = true;

  clientList: any[] = [];
  projectList: any[] = [];
  employeeList: any[] = [];
  chargeOptions: string[] = ['All', 'Charge', 'Non-Charge'];

  constructor(
    private service: TimeEntryForPlanningService,
    private fb: FormBuilder,
    private _store: Store,
    private commonService: CommonService,
    private consultantDetailsService: ConsultantDetailsService,
    private _snackBarService: SnackbarService
  ) { }
  

  // ----- Lifecycle hooks -----

  // On init - loads client/consultant lookups, builds the filter form,
  // wires each control up to the planning service's filter streams, and
  // defaults the employee filter to the logged-in user when there's a match
async ngOnInit(): Promise<void> {

  const today = new Date();
  const periodEnd = new Date();
  periodEnd.setDate(today.getDate() + 30);

  this.entryForm = this.fb.group({
    clientDropDown: [null],
    projectDropDown: [null],
    employeeDropDown: [null],
    dateRange: this.fb.group({
      startDate: [today],
      endDate: [periodEnd],
    }),
    firstCheckbox: [true],
    secondCheckbox: [true],
    chargeDropDown: ['All'],
    
  });

  await this.loadClients();
  await this.loadConsultants();

  this.entryForm.get('firstCheckbox')!.valueChanges.subscribe(value => {
    this.service.setShowClashes(value);
  });

  this.entryForm.get('secondCheckbox')!.valueChanges.subscribe(value => {
    this.service.setShowNonClashed(value);
  });

  this.entryForm.get('chargeDropDown')!.valueChanges.subscribe(value => {
    this.service.setChargedFilter(value ?? 'All');
  });

  this.entryForm.get('clientDropDown')!.valueChanges.subscribe(value => {
    this.service.setClientFilter(value);
    this.loadProjectsForClients(value ?? []);
  });

  this.entryForm.get('projectDropDown')!.valueChanges.subscribe(value => {
    this.service.setProjectFilter(value);
  });

  this.entryForm.get('employeeDropDown')!.valueChanges.subscribe(value => {
    this.service.setEmployeeFilter(value);
  });

  this.entryForm.get('dateRange')!.valueChanges
    .pipe(
      distinctUntilChanged((prev, curr) =>
        prev.startDate?.valueOf?.() === curr.startDate?.valueOf?.() &&
        prev.endDate?.valueOf?.() === curr.endDate?.valueOf?.()
      )
    )
    .subscribe(value => {
      const startDay = value.startDate ? this.stripTime(value.startDate) : null;
      const endDay = value.endDate ? this.stripTime(value.endDate) : null;

      if (
        startDay !== null &&
        endDay !== null &&
        startDay > endDay
      ) {
        this._snackBarService.showSnackBar(
          Constants.SNACKBAR_MSG_INVALID_DATE_RANGE,
          'close',
          'failure'
        );
      }

      const rangeStart = value.startDate
        ? this.formatToISO(value.startDate)
        : null;
      const rangeEnd = value.endDate
        ? this.formatToISO(value.endDate)
        : null;

      this.service.setDateRange(rangeStart, rangeEnd);
    });

  this.service.setDateRange(
    this.formatToISO(today),
    this.formatToISO(periodEnd)
  );

  this.service.setChargedFilter('All');
  this.service.setShowClashes(true);
  this.service.setShowNonClashed(true);

  this._store.select(getCurrentUser)
    .subscribe(user => {
      if (!user) {
        return;
      }

      const match = this.employeeList.find(
        e =>
          e.Employee?.trim().toLowerCase() ===
          user.Employee?.trim().toLowerCase()
      );

      if (match) {
        this.entryForm
          .get('employeeDropDown')!
          .setValue([match.Employee]);
      }
    });

 Promise.resolve().then(() => {
    this.collapsedChange.emit(this.isFullyCollapsed);
  });
}

  ngOnChanges(changes: SimpleChanges): void {
  if (changes['collapsedSummary'] && !this.collapsedSummary) {
    this.manuallyExpanded = false;
  }
  if (changes['collapsedSummary']) {
    Promise.resolve().then(() => {
      this.collapsedChange.emit(this.isFullyCollapsed);
    });
  }
}
  // Loads the client dropdown options
  private async loadClients(): Promise<void> {
    try {
      this.clientList = await this.commonService.getAllClients('planFilterPanel');
    } catch (err) {
      console.error('Failed to load clients', err);
    }
  }

  // Loads the employee dropdown options, deriving a display FullName
  // for each consultant returned by the service
  private async loadConsultants(): Promise<void> {
    try {
      const consultants: any[] = await firstValueFrom(
        this.consultantDetailsService.getConsultantDetails()
      );
      this.employeeList = consultants.map(c => ({
        ...c,
        FullName: `${c.FirstName} ${c.LastName}`.trim()
      }));
    } catch (err) {
      console.error('Failed to load consultants', err);
    }
  }

  // Loads and merges projects for the selected clients, de-duplicating
  // by project code since the same project can span multiple clients
  private async loadProjectsForClients(clientCodes: string[]): Promise<void> {
    if (!clientCodes || clientCodes.length === 0) {
      this.projectList = [];
      return;
    }

    try {
      const results = await Promise.all(
        clientCodes.map(code => this.commonService.getProjectsByClient(code))
      );

      const merged = results.flat();
      const seen = new Set<string>();
      this.projectList = merged.filter(p => {
        if (seen.has(p.Project)) { return false; }
        seen.add(p.Project);
        return true;
      });
    } catch (err) {
      console.error('Failed to load projects', err);
      this.projectList = [];
    }
  }

  get effectiveCollapsed(): boolean {
    return this.collapsedSummary && !this.manuallyExpanded;
  }

  get isManuallyCollapsedRail(): boolean {
    return !this.isFilterOpen && !this.collapsedSummary;
  }
  get isFullyCollapsed(): boolean {
    return !this.isFilterOpen && !this.collapsedSummary;
  }

  toggleSummaryExpand(): void {
    this.manuallyExpanded = !this.manuallyExpanded;
  }

  get headerArrowIcon(): string {
    if (this.collapsedSummary) {
      return 'keyboard_arrow_up';
    }
    return this.isFilterOpen ? 'chevron_left' : 'chevron_right';
  }

  onHeaderArrowClick(): void {
    if (this.collapsedSummary) {
      this.toggleSummaryExpand();
    } else {
      this.toggleFilters();
    }
  }

  toggleFilters(): void {
    this.isFilterOpen = !this.isFilterOpen;
    this.collapsedChange.emit(this.isFullyCollapsed);
  }
  // Formats a Date as YYYY-MM-DD using local date parts
  private formatToISO(date: Date): string {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Zeroes out the time portion so two dates can be compared by day only
  private stripTime(date: Date): number {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }

}